import React, { ReactNode, useContext, useState } from "react";
import colors from "../../shared/constants/color";
import { Minus, Plus } from "react-feather";
import {
  StyledCardContainer,
  StyledCardHeader,
  StyledIconTitleContainer,
  StyledIconWrapper,
  StyledSeparator,
  StyledCardContent,
  StyledCardTitle,
  StyledCardFooter,
} from "./style";
import { Box, SxProps } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { ThemeContext } from "../../context/ThemeContextProvider";
import { getWiserTheme } from "../../shared/theme/wiserTheme";

interface IProps {
  icon?: ReactNode;
  style?: "white" | "neutral" | "aiWhite" | "aiNeutral";
  titleStyle?: "default" | "aiGradient";
  title?: string;
  expandable?: boolean;
  defaultExpanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  headerAction?: ReactNode;
  footerAction?: ReactNode;
  children: ReactNode;
  sx?: SxProps<Theme>;
}

export const XCard: React.FC<IProps> = ({
  icon,
  style = "neutral",
  titleStyle = "default",
  title,
  expandable = false,
  defaultExpanded = false,
  onExpandChange,
  headerAction,
  footerAction,
  children,
  sx,
}) => {
  const { darkModeActive } = useContext(ThemeContext);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const shouldShowHeader = icon || title;
  const shouldShowContent = expandable ? isExpanded : true;
  const iconColor = darkModeActive
    ? getWiserTheme(true).textMuted
    : colors.neutral.N200;

  const toggleExpanded = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onExpandChange?.(newExpandedState);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", ...sx }}>
      <StyledCardContainer $cardStyle={style} $darkModeActive={darkModeActive}>
        {shouldShowHeader && (
          <>
            <StyledCardHeader
              $clickable={expandable}
              onClick={expandable ? toggleExpanded : undefined}
            >
              <StyledIconTitleContainer>
                {icon && <StyledIconWrapper>{icon}</StyledIconWrapper>}
                {title && (
                  <StyledCardTitle
                    $titleStyle={titleStyle}
                    $darkModeActive={darkModeActive}
                  >
                    {title}
                  </StyledCardTitle>
                )}
              </StyledIconTitleContainer>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {headerAction}
                {expandable &&
                  (isExpanded ? (
                    <Minus size={16} color={iconColor} />
                  ) : (
                    <Plus size={16} color={iconColor} />
                  ))}
              </Box>
            </StyledCardHeader>
            {shouldShowContent && (
              <StyledSeparator $darkModeActive={darkModeActive} />
            )}
          </>
        )}
        {shouldShowContent && (
          <StyledCardContent $darkModeActive={darkModeActive}>
            {children}
          </StyledCardContent>
        )}
        {footerAction && <StyledCardFooter>{footerAction}</StyledCardFooter>}
      </StyledCardContainer>
    </Box>
  );
};

export default XCard;
